import React, { useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { injectIntl, IntlShape } from 'react-intl'
import { Link, useLocation, useRouteMatch } from 'react-router-dom'
import Button from 'components/Button'
import Icon from 'components/Icon'
import { selectCurrentUser, selectCurrentZone, selectAvailableZones, setCurrentZone } from 'store/global'
import styles from './Header.scss'
import { useMatomo } from '@datapunt/matomo-tracker-react'
import { exportSingleEntityFile } from 'services/verticalsApi'
import { downloadXLSX } from 'utils/DownloadXLSX'
import Backdrop from '@mui/material/Backdrop'
import CircularProgress from '@mui/material/CircularProgress'
import { Avatar, Button as AntButton, Divider, Popover, Select, Typography } from 'antd'
import { logoutRequest } from './utils'
import localStorageService from 'services/storage'
import history from 'utils/history'

interface HeaderProps {
  intl: IntlShape
  currentZoneIndexes: {
    [key: string]: any
  }
}

interface RouteParams {
  vertical: string
  entityId: string
}

function stringToColor(string: string) {
  let hash = 0
  let i

  /* eslint-disable no-bitwise */
  for (i = 0; i < string.length; i += 1) {
    hash = string.charCodeAt(i) + ((hash << 5) - hash)
  }

  let color = '#'

  for (i = 0; i < 3; i += 1) {
    const value = (hash >> (i * 8)) & 0xff
    color += `00${value.toString(16)}`.slice(-2)
  }
  /* eslint-enable no-bitwise */

  return color
}

function stringAvatar(name: string) {
  const nameParts = name.split(' ')
  const initials = nameParts.length > 1 ? `${nameParts[0][0]}${nameParts[1][0]}` : nameParts[0][0]

  return {
    style: {
      backgroundColor: stringToColor(name),
      width: 24,
      height: 24,
      fontSize: '12px',
      cursor: 'pointer',
    },
    children: initials.toUpperCase(),
  }
}

function Header({ intl, currentZoneIndexes }: HeaderProps) {
  const location = useLocation()
  const dispatch = useDispatch()
  const { trackEvent } = useMatomo()

  const match = useRouteMatch<RouteParams>('/vertical/:vertical/:entityId')

  const [loading, setLoading] = useState(false)
  const currentZone = useSelector(selectCurrentZone)
  const availableZones = useSelector(selectAvailableZones)
  const currentUser = useSelector(selectCurrentUser)

  const { email = '', fullName = '' } = currentUser || {}

  const { name = '' } = currentZone || {}
  const currentZoneName = name

  const onZoneSelect = (zoneId: string) => {
    const nextZoneId = parseInt(zoneId)
    const nextZone = availableZones.find(({ id }: { id: number }) => id === nextZoneId)

    if (nextZone) {
      dispatch(setCurrentZone({ zone: nextZone }))
      localStorageService.setLastSelectedZoneForUser(currentUser, nextZone)
      history.push('/dashboard')
    }
  }

  const onXLSXExport = async () => {
    if (!match?.params) return

    setLoading(true)
    try {
      const entityId = match.params.entityId
      const csvTypes = {
        baseCsvType: `single-entity-${match.params.vertical}`,
        activityCsvType: `activity-${match.params.vertical}`,
      }
      const verticalType = match.params.vertical

      const Base64EntityFile = await exportSingleEntityFile(entityId, currentZoneIndexes, csvTypes, verticalType)
      downloadXLSX(Base64EntityFile, 'single-entity-' + entityId + '.xlsx')
      trackEvent({ category: 'Export', action: 'User requested for single entity XLSX' })
    } catch (error) {
      console.error('Export failed', error)
    } finally {
      setLoading(false)
    }
  }

  const handleLogout = () => {
    logoutRequest().then(res => {
      window.location.href = `${res.data.logoutUrl}?id_token_hint=${res.data.idToken}&post_logout_redirect_uri=${window.location.origin}`
    })
  }

  const accountContent = (
    <div style={{ minWidth: 200 }}>
      <Typography.Title level={5} style={{ margin: '0' }}>
        Account
      </Typography.Title>
      <Divider style={{ margin: '5px 0 16px' }} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
        <Avatar {...stringAvatar(fullName || 'User')} />
        <div>
          <div style={{ fontSize: 14 }}>{fullName || 'User'}</div>
          <div style={{ fontSize: 12 }}>{email}</div>
        </div>
      </div>
      <Divider style={{ margin: '10px 0 12px' }} />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span
          style={{
            fontWeight: 400,
            fontSize: '14px',
          }}
        >
          Log Out
        </span>
        <AntButton size='small' ghost onClick={handleLogout}>
          <Icon name='logout' size='regular' />
        </AntButton>
      </div>
    </div>
  )

  return (
    <>
      <Backdrop open={loading} style={{ zIndex: 1300, color: '#fff' }}>
        <CircularProgress color='inherit' />
      </Backdrop>
      <header className={styles.header}>
        <Link to='/dashboard' color='link' className={styles.logoLink} title='IVIX'>
          <Icon name='logo' size='xl' className={styles.logo} />
        </Link>
        <div className={styles.linksWrapper}>
          <ul className={styles.linksList}>
            <li className={styles.linkItem}>
              <Popover
                content={accountContent}
                placement='bottomLeft'
                trigger='click'
                overlayInnerStyle={{
                  boxShadow: '0px 2px 8px 0px #00000026',
                  borderRadius: 2,
                  padding: '6px 12px',
                }}
              >
                <Button buttonType='transparent' to={undefined} className={styles.profile}>
                  <Avatar {...stringAvatar(fullName || 'User')} />
                </Button>
              </Popover>
            </li>
            <li className={styles.linkItem}>
              {currentZoneName && availableZones.length > 1 && (
                <div className={styles.currentZone}>
                  <Select
                    value={currentZone?.id?.toString()}
                    onChange={onZoneSelect}
                    style={{ minWidth: 120 }}
                    size='small'
                  >
                    {availableZones.map((zone: { id: number; name: string }) => (
                      <Select.Option key={zone.id} value={zone.id.toString()}>
                        {zone.name}
                      </Select.Option>
                    ))}
                  </Select>
                </div>
              )}
            </li>
            {location.pathname.includes('/vertical') && (
              <li>
                <Button buttonType='secondary' className={styles.export} disabled={false} onClick={onXLSXExport}>
                  <Icon name='exportFile' size='regular' />
                  EXPORT
                </Button>
              </li>
            )}
          </ul>
        </div>
      </header>
    </>
  )
}

export default injectIntl(Header)
